import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/elements/button'
import { Checkbox } from '@/components/elements/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/elements/dialog'
import { Switch } from '@/components/elements/switch'
import { getOrganizationAccounts } from '@/services/organization.service'
import { toast } from '@/utils/helpers'

/**
 * Org-controlled assignment of a shared item (an EAP or a custom course) to the
 * organization's facilities (RSCX-320). Turn on "All facilities" to reach every
 * facility (including any added later), or leave it off and pick specific ones.
 * With none selected, the item reaches no facility.
 *
 * The parent supplies the item-specific fetch/save thunks so this component can
 * serve both EAPs and certificates.
 */
const AssignmentDialog = ({
  open,
  setOpen,
  organizationId,
  title,
  fetchAssignment,
  saveAssignment,
  onSaved,
}) => {
  const queryClient = useQueryClient()

  const { data: accounts } = useQuery({
    queryKey: ['organization-accounts', organizationId, 'all'],
    queryFn: () =>
      getOrganizationAccounts({ organizationId, pagination: { pageSize: 1000, pageIndex: 0 } }),
    placeholderData: keepPreviousData,
    enabled: open,
  })
  const facilities = useMemo(() => accounts?.results ?? [], [accounts])

  const { data: assignment, isPending: loadingAssignment } = useQuery({
    queryKey: ['assignment', title],
    queryFn: fetchAssignment,
    enabled: open,
  })

  const [assignedToAll, setAssignedToAll] = useState(false)
  const [selected, setSelected] = useState(() => new Set())

  // Seed local state once the current assignment loads.
  useEffect(() => {
    if (!assignment) return
    setAssignedToAll(Boolean(assignment.assignedToAll))
    setSelected(new Set(assignment.facilityIds ?? []))
  }, [assignment])

  const toggleFacility = (id) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const { isPending: saving, mutate: save } = useMutation({
    mutationFn: () =>
      saveAssignment({
        assignedToAll,
        facilityIds: assignedToAll ? [] : Array.from(selected),
      }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Assignment updated', 'success')
      queryClient.invalidateQueries({ queryKey: ['assignment', title] })
      onSaved?.()
      setOpen(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Choose which facilities can see this. Assigned facilities get it read-only — you keep
            managing the content here, and edits propagate automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 py-1">
          <div>
            <p className="font-medium">All facilities</p>
            <p className="text-muted-foreground text-sm">
              Every facility in the organization, including ones added later.
            </p>
          </div>
          <Switch
            checked={assignedToAll}
            onCheckedChange={setAssignedToAll}
            disabled={saving}
            aria-label="Assign to all facilities"
          />
        </div>

        {!assignedToAll && (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">Specific facilities</p>
            {(() => {
              if (loadingAssignment) {
                return <p className="text-muted-foreground text-sm">Loading…</p>
              }
              if (facilities.length === 0) {
                return (
                  <p className="text-muted-foreground text-sm">
                    This organization has no facilities yet.
                  </p>
                )
              }
              return (
                <ul className="divide-border max-h-72 divide-y overflow-y-auto rounded-md border">
                  {facilities.map((facility) => (
                    <li key={facility.id} className="flex items-center gap-3 p-3">
                      <Checkbox
                        id={`facility-${facility.id}`}
                        checked={selected.has(facility.id)}
                        onCheckedChange={() => toggleFacility(facility.id)}
                        disabled={saving}
                      />
                      <label
                        htmlFor={`facility-${facility.id}`}
                        className="cursor-pointer text-sm"
                      >
                        {facility.name}
                      </label>
                    </li>
                  ))}
                </ul>
              )
            })()}
            {!loadingAssignment && facilities.length > 0 && selected.size === 0 && (
              <p className="text-muted-foreground text-sm">
                No facilities selected — this won&apos;t be visible to anyone yet.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => save()} disabled={saving}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

AssignmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  fetchAssignment: PropTypes.func.isRequired,
  saveAssignment: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
}

export default AssignmentDialog
